/** Zeecore /utilisation/v2 vehicle-day row (flat API shape). */

export interface TruthUtilisationVehicleDayRow {
  date: string;
  vehicle: string;
  vehicle_entity_id?: string;
  vehicle_type?: string;
  total_miles?: number;
  active_hours?: number;
  idle_hours?: number;
  downtime_hours?: number;
  productive_hours?: number;
  num_trips?: number;
  revenue_gbp?: number | null;
  avg_laden_percentage?: number | null;
  utilisation_score: number;
  utilisation_rate?: number | null;
  mileage_efficiency_pct?: number;
  time_efficiency_pct?: number;
  revenue_efficiency_pct?: number | null;
  load_efficiency_pct?: number | null;
  target_mileage_miles?: number;
  target_driving_hours?: number;
  target_waiting_hours?: number;
  target_revenue_gbp?: number | null;
}

export interface TruthUtilisationLastComputed {
  computed_at?: string;
  record_count?: number;
  vehicle_count?: number;
  start_date?: string;
  end_date?: string;
  /** Assumptions fingerprint stamped at compute time */
  assumptions_content_hash?: string;
  config_revision?: number;
}

export interface TruthUtilisationStatusResponse {
  organisation_id: string;
  date_range: { start: string; end: string };
  last_computed: TruthUtilisationLastComputed | null;
  active_job: TruthUtilisationJobRecord | null;
  warnings: string[];
}

export interface TruthUtilisationJobRecord {
  jobId: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  recordCount?: number;
  error?: string;
}

/** Zeecore /utilisation/v2/config — mirrors UtilisationAssumptionsConfig in zeecore. */
export type UtilisationTargetRateBasis = 'body_style' | 'tonnage';

export interface UtilisationAnalysisConfig {
  /** Per body-style map (legacy: single number — coerce on load). */
  target_driving_hours: number | Record<string, number>;
  target_waiting_hours: number | Record<string, number>;
  target_driving_hours_tonnage?: Record<string, number>;
  target_waiting_hours_tonnage?: Record<string, number>;
  target_rate_basis?: UtilisationTargetRateBasis;
  mileage_target_miles: Record<string, number>;
  revenue_target_gbp: Record<string, number>;
  /** Stored in kg; UI shows/edits as tonnes (T). */
  load_capacity_kg: Record<string, number>;
  mileage_target_miles_tonnage?: Record<string, number>;
  revenue_target_gbp_tonnage?: Record<string, number>;
  load_capacity_kg_tonnage?: Record<string, number>;
  component_weights?: {
    mileage: number;
    time: number;
    revenue: number;
    load: number;
  };
  min_stop_duration_mins?: number;
  min_idle_duration_mins?: number;
  movement_threshold_miles?: number;
}

export interface UtilisationAssumptionsConfig {
  version: string;
  updated_at: string;
  updated_by: string;
  /** Monotonic revision — increments when assumption payload changes */
  revision?: number;
  /** Short SHA-256 of assumption payload (excludes meta fields) */
  content_hash?: string;
  utilisation_analysis: UtilisationAnalysisConfig;
  primary_utilisation_metric?: 'utilisation_score' | 'utilisation_rate';
  use_case?: 'logistics' | 'emergency';
}

/** Body-style rows in the assumptions table (includes Default fallback). */
export const UTILISATION_TARGET_VEHICLE_TYPES = [
  'Lorry',
  'Tractor Unit',
  'Rigid Truck',
  'Service Van',
  'Mini Truck',
  'Default',
] as const;

export type UtilisationTargetVehicleType = (typeof UTILISATION_TARGET_VEHICLE_TYPES)[number];

/** Tonnage-class rows — same set as profitability assumptions. */
export const UTILISATION_TARGET_TONNAGE_CLASSES = [
  '3.5T',
  '7.5T',
  '18T',
  '26T',
  'Artic',
  'Default',
] as const;

export type UtilisationTargetTonnageClass = (typeof UTILISATION_TARGET_TONNAGE_CLASSES)[number];

/** Default tonnage maps when config has not been seeded with them yet. */
export const DEFAULT_UTILISATION_TONNAGE_TARGETS = {
  mileage_target_miles_tonnage: {
    '3.5T': 80,
    '7.5T': 120,
    '18T': 150,
    '26T': 200,
    Artic: 220,
    Default: 150,
    default: 150,
  },
  revenue_target_gbp_tonnage: {
    '3.5T': 180,
    '7.5T': 280,
    '18T': 450,
    '26T': 650,
    Artic: 700,
    Default: 500,
    default: 500,
  },
  load_capacity_kg_tonnage: {
    '3.5T': 3500,
    '7.5T': 7500,
    '18T': 18000,
    '26T': 26000,
    Artic: 26000,
    Default: 20000,
    default: 20000,
  },
  target_driving_hours_tonnage: {
    '3.5T': 8,
    '7.5T': 8,
    '18T': 8,
    '26T': 8,
    Artic: 8,
    Default: 8,
    default: 8,
  },
  target_waiting_hours_tonnage: {
    '3.5T': 1,
    '7.5T': 1,
    '18T': 1,
    '26T': 1,
    Artic: 1,
    Default: 1,
    default: 1,
  },
} as const;

/** Default body-style hours when config still has a legacy scalar. */
export const DEFAULT_UTILISATION_BODY_HOURS = {
  target_driving_hours: {
    Lorry: 8,
    'Tractor Unit': 8,
    'Rigid Truck': 8,
    'Service Van': 8,
    'Mini Truck': 8,
    Default: 8,
    default: 8,
  },
  target_waiting_hours: {
    Lorry: 1,
    'Tractor Unit': 1,
    'Rigid Truck': 1,
    'Service Van': 1,
    'Mini Truck': 1,
    Default: 1,
    default: 1,
  },
} as const;